import { Component, computed, effect, inject, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzUploadModule, NzUploadFile } from "ng-zorro-antd/upload";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzMessageService } from "ng-zorro-antd/message";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { CoreDesiredOutcome, CoreCreateDisputeDto, CoreDisputeType, DropdownItem } from "@handshake/core-types";
import { CoreRaiseDisputeModalConfig, CoreDesiredOutcomeOption } from "./core-raise-dispute-modal.config";

interface CoreRaiseDisputeModalData extends CoreRaiseDisputeModalConfig {}

@Component({
  selector: "app-core-raise-dispute-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzInputModule,
    NzSelectModule,
    NzUploadModule,
    NzIconModule,
  ],
  templateUrl: "./core-raise-dispute-modal.component.html",
  styles: [
    `
      :host ::ng-deep .ant-upload {
        width: 100%;
      }
      :host ::ng-deep .ant-upload-drag {
        background: #fafafa;
        border: 1px dashed #d9d9d9;
        border-radius: 8px;
      }
      :host ::ng-deep .ant-upload-drag:hover {
        border-color: #1890ff;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selector {
        min-height: 40px;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selection-item {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.5;
        padding: 6px 12px;
        display: flex;
        align-items: center;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selection-placeholder {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.5;
        padding: 6px 12px;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown {
        max-height: 400px !important;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        padding: 12px 16px;
        margin: 0;
        line-height: 1.6;
        min-height: 48px;
        height: auto;
        display: flex;
        align-items: center;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item:hover {
        background-color: #f5f5f5;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item-option-selected {
        background-color: #e6f7ff;
        font-weight: 500;
      }
    `,
  ],
})
export class CoreRaiseDisputeModalComponent {
  readonly modalRef = inject(NzModalRef);
  readonly fb = inject(FormBuilder);
  readonly nzMessage = inject(NzMessageService);
  readonly data = inject<CoreRaiseDisputeModalData>(NZ_MODAL_DATA);

  currentPage = signal(0); // 0 = Page 1 (Form), 1 = Page 2 (Confirmation)
  isSubmitting = signal(false);
  files = signal<File[]>([]);
  desiredOutcomeValue = signal<CoreDesiredOutcome | null>(null);

  form: FormGroup = this.fb.group({
    issueDescription: ["", [Validators.required]],
    desiredOutcome: ["", [Validators.required]],
    reason: [""],
  });

  // Convert options to dropdown items
  dropdownItems: DropdownItem[] = this.data.desiredOutcomeOptions.map((opt) => ({
    code: opt.value,
    label: opt.label,
  }));

  // Get selected option config - now properly reactive
  selectedOption = computed(() => {
    const selectedValue = this.desiredOutcomeValue();
    return this.data.desiredOutcomeOptions.find((opt) => opt.value === selectedValue);
  });

  // Effect to update reason field validation based on selected option
  private reasonValidationEffect = effect(() => {
    const option = this.selectedOption();
    const reasonControl = this.form.get("reason");

    if (option?.requiresReason) {
      reasonControl?.setValidators([Validators.required]);
    } else {
      reasonControl?.clearValidators();
    }
    reasonControl?.updateValueAndValidity();
  });

  constructor() {
    // Watch desired outcome changes and update signal
    this.form.get("desiredOutcome")?.valueChanges.subscribe((value) => {
      this.desiredOutcomeValue.set(value);
      // Reset reason when outcome changes
      this.form.get("reason")?.setValue("");
    });
  }

  beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
    const actualFile = file.originFileObj || (file as any);
    if (!actualFile || !(actualFile instanceof File)) {
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "application/pdf"];

    if (actualFile.size > maxSize) {
      this.nzMessage.error(`${actualFile.name}: File size must be less than 10MB`);
      return false;
    }

    if (!allowedTypes.includes(actualFile.type)) {
      this.nzMessage.error(`${actualFile.name}: File type must be JPEG, PNG, PDF, or GIF`);
      return false;
    }

    this.files.update((current) => {
      if (!current.find((f) => f.name === actualFile.name && f.size === actualFile.size)) {
        return [...current, actualFile];
      }
      return current;
    });

    return false; // Prevent auto upload
  };

  removeFile(index: number): void {
    this.files.update((current) => current.filter((_, i) => i !== index));
  }

  next(): void {
    if (this.currentPage() === 0) {
      // Validate form before proceeding
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      this.currentPage.set(1);
    }
  }

  back(): void {
    if (this.currentPage() === 1) {
      this.currentPage.set(0);
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const formValue = this.form.value;
    const disputeData: CoreCreateDisputeDto = {
      projectMilestoneId: this.data.projectMilestoneId,
      disputeType: CoreDisputeType.INITIAL_ATTEMPT, // Default, can be made configurable
      describeIssue: formValue.issueDescription,
      desiredOutcome: formValue.desiredOutcome,
      expectation: formValue.reason || undefined,
    };

    this.data.submitRequest(disputeData, this.files()).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.modalRef.close({ success: true });
      },
      error: (error) => {
        this.isSubmitting.set(false);
        this.nzMessage.error(error?.error?.message || "Failed to submit dispute");
      },
    });
  }

  onCancel(): void {
    this.modalRef.close();
  }
}
